/**
 * Business — placeholder page at `/business` with the 4 business
 * categories from the reference mockup. Two are live (Dealers, Workshops)
 * and route into their respective login pages; the other two show a
 * "Coming Soon" badge.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Wrench, Cog, PackageSearch, ArrowRight } from 'lucide-react';
import PropitstopNav from '../components/PropitstopNav';
import PropitstopFooter from '../components/PropitstopFooter';

const CARDS = [
  {
    icon: Car,
    label: 'Used Car Dealers',
    desc: 'Manage inventory, get more leads, sell faster with our AI-powered dealer platform.',
    cta: 'Learn more', to: '/business/drive',
    live: true,
  },
  {
    icon: Wrench,
    label: 'Workshops',
    desc: 'Pitcrew GMS — streamline jobs, invoices, customers and inventory in one place.',
    cta: 'Learn more', to: '/business/pitcrew',
    live: true,
  },
  {
    icon: Cog,
    label: 'Spare Parts Suppliers',
    desc: 'Reach more customers and grow your parts business.',
    cta: 'Notify me', to: '#',
    live: false,
  },
  {
    icon: PackageSearch,
    label: 'Accessories Businesses',
    desc: 'Expand your reach and boost your sales with a modern digital storefront.',
    cta: 'Notify me', to: '#',
    live: false,
  },
];

export default function Business() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="business-page">
      <PropitstopNav />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 text-center">
        <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-bold">For Automotive Businesses</div>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900 leading-tight">
          Grow with Propitstop
        </h1>
        <p className="mt-4 text-zinc-500 max-w-2xl mx-auto">
          Whichever side of the automotive market you&apos;re on — dealer, garage, spare parts or accessories — we&apos;re building the modern digital rails for you to grow on.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid md:grid-cols-2 gap-5 lg:gap-6">
        {CARDS.map((c) => (
          <div
            key={c.label}
            data-testid={`biz-card-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="relative bg-blue-50/50 border border-blue-100 rounded-3xl p-6 lg:p-8 flex flex-col"
          >
            {!c.live && (
              <span className="absolute top-5 right-5 inline-block px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-600 text-[10px] font-bold uppercase tracking-wider">
                Coming Soon
              </span>
            )}
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border border-blue-100">
              <c.icon className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight">{c.label}</h2>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed flex-1">{c.desc}</p>
            {c.live ? (
              <Link
                to={c.to}
                className="mt-5 self-start inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-bold transition-colors"
              >
                {c.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button
                disabled
                className="mt-5 self-start inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-200 text-zinc-500 text-sm font-bold cursor-not-allowed"
              >
                {c.cta}
              </button>
            )}
          </div>
        ))}
      </section>

      <PropitstopFooter />
    </div>
  );
}
